import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';
import { CodiceTipoPagamento } from '../../constants/tipo-pagamemto';
import { AttoPignoramnetoDTO } from '../../models/atto-pignoramento-dto';
import { fromIdAndTipo, ModalitaPagamentoDTO } from '../../models/modalita-pagamento-dto';
import { PagamentoDTO } from '../../models/pagamento-dto';
import { Localita } from '../../models/portale/localita';
import { TipologicaDTO } from '../../models/tipologica-d-t-o';
import { ComponentOutputStatus } from '../../normalizzazione/component-output-status';
import { MessageService } from '../../services/message.service';
import { ComponentReducer } from '../../utils/component-reducer';
import { toBonificoExtraSepa, toBonificoSepa, toTesoreria, toVagliaBdi } from './modalita-pagamento-mapper';

@Component({
	selector: 'lib-pagamento',
	templateUrl: './pagamento.component.html',
	styleUrls: ['./pagamento.component.css']
})
export class PagamentoComponent implements OnInit {

	form: FormGroup;

	@Input()
	pagamento: PagamentoDTO;

	@Input()
	tipologiePagamento$: Observable<Array<TipologicaDTO>>;

	@Input()
	modalitaPagamento$: Observable<Array<ModalitaPagamentoDTO>>;

	@Input()
	province: Observable<Array<Localita>>;

	@Input()
	attoPignoramento: AttoPignoramnetoDTO;

	@Output()
	nextStep: EventEmitter<PagamentoDTO> = new EventEmitter<PagamentoDTO>();

	@Output()
	salvaBozza: EventEmitter<PagamentoDTO> = new EventEmitter<PagamentoDTO>();

	@Output()
	previousStep: EventEmitter<PagamentoDTO> = new EventEmitter<PagamentoDTO>();

	tipoModalitaPagamento: FormControl;
	modalitaPagamento: FormControl;
	patientCategory: FormControl;
	direction: string = 'both-ways';
	isFinal: boolean = false;

	changesVagliaBdi$ = new Subject<ComponentOutputStatus<(value: PagamentoDTO) => void>>();

	isFormValid$: Observable<boolean>;

	defaultValueModalitaRimborso: ModalitaPagamentoDTO;

	modalitaPagamentoPerTipoSelezionato$: Observable<Array<ModalitaPagamentoDTO>>;

	changesAttoPignoramento$ = new Subject<ComponentOutputStatus<(value: PagamentoDTO) => void>>();

	constructor(private msg: MessageService) { }

	ngOnInit() {
		this.form = new FormGroup({});
		let valid = true;

		if (!this.pagamento) {
			this.initPagamento();
			valid = false;
		}

		// console.log('[PagamentoComponent] onInit', this.pagamento);

		this.tipoModalitaPagamento = new FormControl({ value: this.pagamento.tipoPagamento, disabled: false },
			[Validators.required]);
		this.form.addControl('tipoModalitaPagamento', this.tipoModalitaPagamento);

		const initalModalitaPagamento = fromIdAndTipo(this.pagamento.idModalitaPagamento, this.pagamento.tipoPagamento);

		this.modalitaPagamento = new FormControl({ value: initalModalitaPagamento, disabled: false },
			[Validators.required]);
		this.form.addControl('modalitaPagamento', this.modalitaPagamento);

		const changesTipoModalitaPagamento$ = this.tipoModalitaPagamento.valueChanges
			.pipe(
				shareReplay()
			);

		this.modalitaPagamentoPerTipoSelezionato$ = changesTipoModalitaPagamento$
			.pipe(
				tap(_ => this.modalitaPagamento.setValue(null)),
				tap(_ => this.resetPagamenti()),
				startWith(this.pagamento.tipoPagamento),
				switchMap(tipoModalitaPagamento =>
					this.modalitaPagamento$.pipe(
						map(modalitaPagamentoPF => {
							const modalitaPagamentoPerTipo = modalitaPagamentoPF.filter(
								mod => tipoModalitaPagamento == mod.tipologiaPagamento.codice
							);
							return modalitaPagamentoPerTipo;
						})
					)
				)
			)

		const changesTipoRimborso$ = changesTipoModalitaPagamento$.pipe(
			map(tipoRimborso => {
				return ComponentOutputStatus.of(
					tipoRimborso != null,
					(pagamento: PagamentoDTO) => pagamento.tipoPagamento = tipoRimborso)
			})
		);

		const changesModalitaRimborso$ =
			this.modalitaPagamento.valueChanges.pipe(
				map(rimborso => {
					return ComponentOutputStatus.of(
						true,
						_ => {
							// console.log('[PagamentoComponent] rimborso', rimborso);
							if (rimborso != undefined && rimborso != '') {
								this.setRimborso(rimborso);
							}
						})
				})
			);

		const pagamentoChanges$: Observable<ComponentOutputStatus<PagamentoDTO>> =
			ComponentReducer.reducer4(
				changesTipoRimborso$,
				changesModalitaRimborso$,
				this.changesVagliaBdi$,
				//TODO
				this.changesAttoPignoramento$,
				this.pagamentoValid(),
				ComponentOutputStatus.of(valid, this.pagamento)
			)
				.pipe(shareReplay());

		this.isFormValid$ = pagamentoChanges$.pipe(map(_ => _.status), startWith(valid));

		pagamentoChanges$.subscribe((pagStatus) => {
			console.log("[PagamentoComponent] status", pagStatus)
		});
	}

	private pagamentoValid(): (value: any) => boolean {
		return pagamento => {
			let pagamentoValid: boolean;
			if (pagamento.tipoPagamento) {

				switch (pagamento.tipoPagamento) {
					case (CodiceTipoPagamento.CODICE_BONIFICO_SEPA):
						pagamentoValid = pagamento.bonifico != null;
						break;
					case (CodiceTipoPagamento.CODICE_BONIFICO_EXTRA_SEPA):
						pagamentoValid = pagamento.bonificoExtraSepa != null;
						break;
					case (CodiceTipoPagamento.CODICE_TESORERIA):
						pagamentoValid = pagamento.tesoreria != null;
						break;
					case (CodiceTipoPagamento.CODICE_VAGLIA):
						pagamentoValid = pagamento.vagliaBDI != null;
						break;
					default:
						pagamentoValid = false;
				}
			}
			else {
				pagamentoValid = false;
			}

			return pagamentoValid;
		};
	}

	modalitaComparator(m1: ModalitaPagamentoDTO, m2: ModalitaPagamentoDTO,) {
		return (m1 || {}).idModalitaPagamento == (m2 || {}).idModalitaPagamento;
	}

	resetPagamenti() {
		this.changesVagliaBdi$.next(
			ComponentOutputStatus.of(true, (p: PagamentoDTO) => p.vagliaBDI = null)
		);
	}

	setRimborso(rimborso: ModalitaPagamentoDTO) {
		// console.log('[setRimborso]', rimborso);
		this.resetModalitaPagamento();
		this.pagamento.idModalitaPagamento = rimborso.idModalitaPagamento;
		switch (rimborso.tipologiaPagamento.codice) {
			case (CodiceTipoPagamento.CODICE_BONIFICO_SEPA):
				this.pagamento.bonifico = toBonificoSepa(rimborso);
				break;
			case (CodiceTipoPagamento.CODICE_BONIFICO_EXTRA_SEPA):
				this.pagamento.bonificoExtraSepa = toBonificoExtraSepa(rimborso);
				break;
			case (CodiceTipoPagamento.CODICE_TESORERIA):
				this.pagamento.tesoreria = toTesoreria(rimborso);
				break;
			case (CodiceTipoPagamento.CODICE_VAGLIA):
				this.pagamento.vagliaBDI = toVagliaBdi(rimborso);
				break;
			default:
		}
	}

	resetModalitaPagamento() {
		this.pagamento.idModalitaPagamento = null;
		this.pagamento.vagliaBDI = null;
		this.pagamento.tesoreria = null;
		this.pagamento.bonificoExtraSepa = null;
		this.pagamento.bonifico = null;
	}

	public hasSelectedModalitaDiPagamentoAnd(v: boolean) {
		return this.hasSelectedModalitaDiPagamento() && v;
	}
	public hasSelectedModalitaDiPagamento(): boolean {
		return this.modalitaPagamento.value;
	}

	//TODO
	public isAutoritaGiudiziaria() {
		return this.hasSelectedModalitaDiPagamentoAnd(
			CodiceTipoPagamento.CODICE_BONIFICO_SEPA == this.tipoModalitaPagamento.value
		)
	}

	forward(_) {
		this.nextStep.emit(this.pagamento);
	}

	onSalvaBozza(_) {
		this.salvaBozza.emit(this.pagamento);
	}

	backward(_) {
		this.previousStep.emit(this.pagamento);
	}

	complete(_) { }

	initPagamento() {
		this.pagamento = new PagamentoDTO();
	}

	//TODO
	attoPignoramentoReady(attoPignoramnetoStatus: ComponentOutputStatus<AttoPignoramnetoDTO>) {
		// console.log('[attoPignoramentoReady]', attoPignoramnetoStatus);
		this.changesAttoPignoramento$.next(
			attoPignoramnetoStatus.mapValue(
				(pign) => (at: PagamentoDTO) =>
					at.tribunale = pign
			)
		);
	}
}
